import React, { useMemo } from 'react'
import type { InlineInputContentOptions, InlineInputProps } from '@ztechapps/webui.input'
import { InlineInput } from '@ztechapps/webui.input'
import { BlogCategorySelect } from './BlogCategorySelect'
import { buildBlogCategoryPathLabel } from './blogCategoryTree.utils'
import { useBlogCategoryList } from './useBlogCategoryList'
import { useBlogCategoryTree } from './useBlogCategoryTree'

/** The blog's category as a path label, picked from the tenant's blog category tree and saved as `{ _id }`. */
export const BlogCategoryInlineInput = ({
  parentInputComponent,
  displayContent,
  name = 'category',
  updateParams,
  initialValues,
  ...rest
}: InlineInputProps) => {
  const { list } = useBlogCategoryList()
  const { flat } = useBlogCategoryTree({ categories: list })
  const categoryId = initialValues?.[name]?._id
  const documentId = initialValues?._id

  // The editor's draft resets whenever the value's identity changes, so the option is rebuilt only when its inputs do.
  const optionValues = useMemo(
    () => ({
      _id: documentId,
      [name]: categoryId ? { value: categoryId, label: buildBlogCategoryPathLabel(flat, categoryId) } : undefined,
    }),
    [documentId, name, categoryId, flat]
  )

  const ParentInput = parentInputComponent || InlineInput

  return (
    <ParentInput
      {...{
        ...rest,
        name,
        initialValues: optionValues,
        displayContent: displayContent || ((option) => option?.label),
        // A pick saves at once; blurring without one closes the editor unchanged, Esc cancels.
        editContent: ({ value, submitting, onBlur, onKeyDown, setNewValue, saveNow }: InlineInputContentOptions) => (
          <BlogCategorySelect
            {...{
              flat,
              value,
              submitting,
              onBlur,
              onKeyDown,
              autoFocus: true,
              onChange: (option) => {
                setNewValue(option)
                return saveNow()
              },
            }}
          />
        ),
        updateParams:
          updateParams ||
          (({ _id, updatedValue }) => ({
            input: {
              _id,
              [name]: { _id: updatedValue.value },
            },
          })),
      }}
    />
  )
}
